import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { hasRole, SETTINGS_FINANCE_ROLES } from '@/auth/roles';
import { getSchoolCostHistory, getSchoolCosts, setSchoolCost } from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { Banner, type BannerTone } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import { showSuccessToast } from '@/lib/toast';
import type { SchoolCostRow, SchoolCostVersion } from '@/types/shared';
import './SchoolCostManager.css';

function schoolLabel(
  row: { schoolLabelAr: string; schoolLabelEn: string } | null | undefined,
  language: string,
): string {
  if (!row) return '';
  return language.startsWith('ar') ? row.schoolLabelAr : row.schoolLabelEn;
}

/**
 * School Costs tab (`/settings/finance`) — the academy cost the school is owed, now PER GRADE
 * (2026-07). Each school is configured by setting a cost for EVERY active grade in one batch; a
 * school counts as configured only when all grades have a cost. Edit + History are Admin-only.
 */
export function SchoolCostManager() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, SETTINGS_FINANCE_ROLES);

  const [schools, setSchools] = useState<SchoolCostRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [banner, setBanner] = useState<{ tone: BannerTone; text: string } | null>(null);

  const [panelSchool, setPanelSchool] = useState<SchoolCostRow | null>(null);
  // Per-grade cost inputs, keyed by gradeId (as string, from the number input).
  const [gradeInputs, setGradeInputs] = useState<Record<number, string>>({});
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [historySchool, setHistorySchool] = useState<SchoolCostRow | null>(null);
  const [history, setHistory] = useState<SchoolCostVersion[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getSchoolCosts()
      .then((res) => {
        if (!cancelled) setSchools(res.schools);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('financeSettings.schoolCosts.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  const filtered = (schools ?? []).filter((row) => {
    const needle = searchInput.trim().toLocaleLowerCase();
    if (!needle) return true;
    return (
      row.schoolLabelAr.toLocaleLowerCase().includes(needle) ||
      row.schoolLabelEn.toLocaleLowerCase().includes(needle)
    );
  });

  function openPanel(row: SchoolCostRow) {
    setBanner(null);
    setHistorySchool(null);
    const inputs: Record<number, string> = {};
    for (const g of row.grades) inputs[g.gradeId] = g.costAmount ?? '';
    setGradeInputs(inputs);
    setEffectiveFrom('');
    setSaveError(null);
    setPanelSchool(row);
  }

  function closePanel() {
    setPanelSchool(null);
    setSaveError(null);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!panelSchool) return;
    setSaveError(null);

    // All grades required, each a valid non-negative number.
    const costs = panelSchool.grades.map((g) => ({
      gradeId: g.gradeId,
      value: (gradeInputs[g.gradeId] ?? '').trim(),
    }));
    const invalid = costs.some((c) => c.value === '' || Number.isNaN(Number(c.value)) || Number(c.value) < 0);
    if (invalid) {
      setSaveError(t('financeSettings.schoolCosts.allGradesRequired'));
      return;
    }

    setSaving(true);
    try {
      const updated = await setSchoolCost(panelSchool.schoolId, {
        effectiveFrom: effectiveFrom.trim() || undefined,
        costs: costs.map((c) => ({ gradeId: c.gradeId, costAmount: c.value })),
      });
      setSchools((prev) =>
        prev ? prev.map((s) => (s.schoolId === updated.schoolId ? updated : s)) : prev,
      );
      const message = t('financeSettings.schoolCosts.saveSuccess');
      setBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      setPanelSchool(null);
      setHistory(null);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setSaveError(
        apiError.code === 'NETWORK_ERROR'
          ? t('financeSettings.schoolCosts.networkError')
          : apiError.message || t('financeSettings.schoolCosts.genericError'),
      );
    } finally {
      setSaving(false);
    }
  }

  function fetchHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    getSchoolCostHistory()
      .then((res) => setHistory(res.versions))
      .catch(() => setHistoryError(t('financeSettings.schoolCosts.history.loadError')))
      .finally(() => setHistoryLoading(false));
  }

  function openHistory(row: SchoolCostRow) {
    setPanelSchool(null);
    setHistorySchool(row);
    setHistory(null);
    fetchHistory();
  }

  function closeHistory() {
    setHistorySchool(null);
  }

  function configuredLabel(row: SchoolCostRow): string {
    const set = row.grades.filter((g) => g.costAmount !== null).length;
    return t('financeSettings.schoolCosts.configuredCount', { set, total: row.grades.length });
  }

  const columns: DataTableColumn<SchoolCostRow>[] = [
    {
      key: 'school',
      header: t('financeSettings.schoolCosts.columns.school'),
      toggleable: false,
      render: (row) => <bdi>{schoolLabel(row, i18n.language)}</bdi>,
    },
    {
      key: 'status',
      header: t('financeSettings.schoolCosts.columns.currentCost'),
      render: (row) =>
        row.fullyConfigured ? (
          <StatusChip label={t('financeSettings.schoolCosts.fullyConfigured')} tone="success" />
        ) : (
          <StatusChip label={configuredLabel(row)} tone="neutral" />
        ),
    },
    {
      key: 'actions',
      header: t('financeSettings.schoolCosts.columns.actions'),
      toggleable: false,
      cellClassName: 'fa-school-costs__actions-cell',
      render: (row) =>
        canEdit ? (
          <div className="fa-school-costs__row-actions">
            <button type="button" className="fa-btn fa-btn--ghost" onClick={() => openPanel(row)}>
              {row.fullyConfigured
                ? t('financeSettings.schoolCosts.editButton')
                : t('financeSettings.schoolCosts.setCostButton')}
            </button>
            <button type="button" className="fa-btn fa-btn--secondary" onClick={() => openHistory(row)}>
              {t('financeSettings.schoolCosts.historyButton')}
            </button>
          </div>
        ) : (
          '—'
        ),
    },
  ];

  const emptyState = searchInput.trim() ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('financeSettings.schoolCosts.empty.noMatchesTitle')}
      hint={t('financeSettings.schoolCosts.empty.noMatchesHint')}
      action={
        <button type="button" className="fa-btn fa-btn--secondary" onClick={() => setSearchInput('')}>
          {t('financeSettings.schoolCosts.empty.clearSearch')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('financeSettings.schoolCosts.empty.noSchoolsTitle')}
      hint={t('financeSettings.schoolCosts.empty.noSchoolsHint')}
    />
  );

  const historyVersions = (history ?? []).filter((v) => v.schoolId === historySchool?.schoolId);

  const historyColumns: DataTableColumn<SchoolCostVersion>[] = [
    {
      key: 'grade',
      header: t('financeSettings.schoolCosts.gradeColumn'),
      render: (v) => <bdi>{i18n.language.startsWith('ar') ? v.gradeLabelAr : v.gradeLabelEn}</bdi>,
    },
    {
      key: 'effectiveFrom',
      header: t('financeSettings.schoolCosts.history.columns.effectiveFrom'),
      render: (v) => <bdi>{formatDateOnly(v.effectiveFrom, i18n.language)}</bdi>,
    },
    {
      key: 'cost',
      header: t('financeSettings.schoolCosts.history.columns.cost'),
      render: (v) => <bdi>{formatCurrencyEGP(Number(v.costAmount), i18n.language)}</bdi>,
    },
    {
      key: 'current',
      header: t('financeSettings.schoolCosts.history.columns.current'),
      render: (v) =>
        v.isCurrent ? (
          <StatusChip label={t('financeSettings.schoolCosts.history.currentBadge')} tone="success" />
        ) : null,
    },
  ];

  return (
    <div className="fa-school-costs">
      {banner && (
        <Banner tone={banner.tone} assertive={banner.tone === 'danger'}>
          {banner.text}
        </Banner>
      )}

      {panelSchool && (
        <form className="fa-form-section fa-school-costs__panel" onSubmit={handleSave} noValidate>
          <h2 className="fa-form-section__title">
            {t('financeSettings.schoolCosts.formTitleGrades', {
              school: schoolLabel(panelSchool, i18n.language),
            })}
          </h2>
          <Banner tone="info">{t('financeSettings.schoolCosts.allGradesRequired')}</Banner>

          <div className="fa-school-costs__grade-grid">
            {panelSchool.grades.map((g) => (
              <label key={g.gradeId} className="fa-field">
                <span className="fa-field__label">
                  <bdi>{i18n.language.startsWith('ar') ? g.gradeLabelAr : g.gradeLabelEn}</bdi>
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="fa-ltr-isolate"
                  value={gradeInputs[g.gradeId] ?? ''}
                  disabled={saving}
                  onChange={(e) => setGradeInputs((prev) => ({ ...prev, [g.gradeId]: e.target.value }))}
                />
              </label>
            ))}
          </div>

          <div className="fa-field-row">
            <label className="fa-field">
              <span className="fa-field__label">
                {t('financeSettings.schoolCosts.effectiveFromLabel')}
              </span>
              <input
                type="date"
                value={effectiveFrom}
                disabled={saving}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </label>
          </div>

          <div className="fa-form-actions">
            <button type="button" className="fa-btn fa-btn--secondary" disabled={saving} onClick={closePanel}>
              {t('financeSettings.schoolCosts.cancelButton')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
              {saving ? t('financeSettings.schoolCosts.savingButton') : t('financeSettings.schoolCosts.saveButton')}
            </button>
          </div>
          {saveError && (
            <p className="fa-field__error" role="alert">
              {saveError}
            </p>
          )}
        </form>
      )}

      {historySchool && (
        <div className="fa-school-costs__history">
          <div className="fa-school-costs__history-header">
            <h2 className="fa-school-costs__history-title">
              {t('financeSettings.schoolCosts.history.title', {
                school: schoolLabel(historySchool, i18n.language),
              })}
            </h2>
            <button type="button" className="fa-btn fa-btn--secondary" onClick={closeHistory}>
              {t('financeSettings.schoolCosts.history.closeButton')}
            </button>
          </div>
          <DataTable
            columns={historyColumns}
            rows={historyVersions}
            rowKey={(v) => v.versionId}
            caption={t('financeSettings.schoolCosts.history.tableCaption', {
              school: schoolLabel(historySchool, i18n.language),
            })}
            meta={null}
            loading={historyLoading}
            error={historyError}
            onRetry={fetchHistory}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            showExportButton={false}
            emptyState={
              <EmptyState
                icon={BiSolidInbox}
                title={t('financeSettings.schoolCosts.history.empty.title')}
                hint={t('financeSettings.schoolCosts.history.empty.hint')}
              />
            }
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(row) => String(row.schoolId)}
        caption={t('financeSettings.schoolCosts.tableCaption')}
        meta={null}
        loading={loading}
        error={loadError}
        onRetry={refetch}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('financeSettings.schoolCosts.searchPlaceholder'),
          ariaLabel: t('financeSettings.schoolCosts.searchAriaLabel'),
        }}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        showExportButton={false}
        emptyState={emptyState}
      />
    </div>
  );
}
